import * as React from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { Table } from "antd";
import { Currency } from "swan-public-api-client";
import { PaginationConfig, TableLocale, ColumnProps } from "antd/lib/table";
import { SpinProps } from "antd/lib/spin";
import { getReferenceSorter, getConsiderationSorter, getDateSorter, getNumberSorter } from "../Grid/Sorters";
import { considerationRenderer, currencyNameRenderer, dateTimeRenderer, priceWithCurrencyViaRecordRenderer, currencyViaRecordRenderer } from "../Grid/Renderers";
import { TradeHistoryGridRow } from "./TradeHistoryGridRow";
import { openingOrClosingToStringConverter } from "../Localisation/Converters";

interface Props extends WrappedComponentProps {
  loading?: boolean | SpinProps;
  bordered?: boolean;
  locale?: TableLocale;
  pagination?: PaginationConfig | false;
  history: TradeHistoryGridRow[];
  baseCurrency: Currency;
  currencies: Map<number, Currency>;
}

function TradeHistoryGridCmp(props: Props) {
  const columns: Array<ColumnProps<TradeHistoryGridRow>> = [
    {
      dataIndex: "Reference",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.reference" }),
      sorter: getReferenceSorter(x => x.Reference),
      sortDirections: ["ascend", "descend"],
      defaultSortOrder: "ascend"
    },
    {
      dataIndex: "MarketName",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.marketName" }),
      sorter: (a, b) => a.MarketName < b.MarketName ? -1 : 1,
      sortDirections: ["ascend", "descend"]
    },
    {
      dataIndex: "OpeningOrClosing",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.openingOrClosing" }),
      render: (value, record) => {
        return openingOrClosingToStringConverter(record.OpeningOrClosing, props.intl);
      },
      sortDirections: ["ascend", "descend"]
    },
    {
      dataIndex: "OriginalQuantity",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.quantity" }),
      sorter: (a, b) => a.OriginalQuantity < b.OriginalQuantity ? -1 : 1,
      sortDirections: ["ascend", "descend"]
    },
    {
      dataIndex: "CurrencyId",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.currency" }),
      render: currencyNameRenderer(x => x.CurrencyId, props.currencies, props.intl),
      sortDirections: ["ascend", "descend"]
    },
    {
      dataIndex: "BookingTime",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.bookingTime" }),
      sorter: getDateSorter(x => x.BookingTime),
      sortDirections: ["ascend", "descend"],
      render: dateTimeRenderer(x => x.BookingTime)
    },
    {
      dataIndex: "BookingPrice",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.bookingPrice" }),
      sorter: (a, b) => a.BookingPrice < b.BookingPrice ? -1 : 1,
      sortDirections: ["ascend", "descend"],
      render: priceWithCurrencyViaRecordRenderer(x => x.BookingPrice, _ => undefined, props.currencies),
      align: "right"
    },
    {
      dataIndex: "Pnl",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.profitAndLoss" }),
      sorter: getNumberSorter(x => x.Pnl),
      sortDirections: ["ascend", "descend"],
      render: currencyViaRecordRenderer(x => x.Pnl, x => x.PnlCurrencyId, props.currencies),
      align: "right"
    },
    {
      dataIndex: "Consideration.BaseConsideration.Value",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.baseCurrencyConsideration" }),
      sorter: getConsiderationSorter(x => x.Consideration.BaseConsideration),
      sortDirections: ["ascend", "descend"],
      render: considerationRenderer(
        x => x.Consideration.BaseConsideration,
        props.currencies
      ),
      align: "right"
    },
    {
      dataIndex: "Consideration.DeliveryConsideration.Value",
      title: props.intl.formatMessage({ id: "tradeHistoryGrid.deliveryCurrencyConsideration" }),
      sorter: getConsiderationSorter(x => x.Consideration.DeliveryConsideration),
      sortDirections: ["ascend", "descend"],
      render: considerationRenderer(
        x => x.Consideration.DeliveryConsideration,
        props.currencies
      ),
      align: "right"
    }
  ];

  return (
    <Table
      loading={props.loading}
      bordered={props.bordered}
      pagination={props.pagination}
      dataSource={props.history}
      rowKey={row => row.InstructionKey.Key}
      locale={props.locale}
      columns={columns}
    />
  );
}

export const TradeHistoryGrid = injectIntl(TradeHistoryGridCmp);
